import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconFileText, IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useNavigate } from 'react-router-dom';
import { useContractsList, type ListFilters } from '../../queries/contracts';
import { useProjectStore } from '../../project/project-store';
import { ContractFormDrawer } from './ContractFormDrawer';
import { dateToYmd, formatContractStatus, formatContractType } from './contract-status';
import type { Contract, ContractStatus } from '../../types/api';

const STATUS_OPTIONS: Array<{ value: ContractStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'in_renewal', label: 'In renewal' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
];

export function ContractsListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | ''>('');
  const [expiring90, setExpiring90] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo<ListFilters>(() => {
    const f: ListFilters = { limit: 200 };
    if (q.trim()) f.q = q.trim();
    if (statusFilter) f.status = statusFilter;
    if (expiring90) {
      const cutoff = new Date();
      cutoff.setUTCDate(cutoff.getUTCDate() + 90);
      f.expiring_before = dateToYmd(cutoff);
    }
    return f;
  }, [q, statusFilter, expiring90]);

  const { data: contracts = [], isLoading } = useContractsList(filters);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Contracts</Title>
        <Text c="dimmed">Pick a project to view its contracts.</Text>
      </Stack>
    );
  }

  function onRowClick(c: Contract) {
    navigate(`/contracts/${c.id}`);
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Contracts</Title>
          <Text c="dimmed" size="sm">
            Project-scoped agreements. Today&apos;s focus is right-of-way (ROW) tracking; future
            slices add surface use, JOAs, farmouts, and more contract types.
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)}>
            New contract
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search title or contract #"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
        <Select
          data={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={statusFilter}
          onChange={(v) => setStatusFilter((v as ContractStatus | '') ?? '')}
          w={180}
          aria-label="Filter by status"
        />
        <Switch
          label="Expiring within 90 days"
          checked={expiring90}
          onChange={(e) => setExpiring90(e.currentTarget.checked)}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={contracts}
        idAccessor="id"
        noRecordsText="No contracts yet"
        onRowClick={({ record }) => onRowClick(record)}
        columns={[
          {
            accessor: 'title',
            title: 'Title',
            render: (c) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {c.title}
                </Text>
                {c.contract_number && (
                  <Text size="xs" c="dimmed">
                    {c.contract_number}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'contract_type',
            title: 'Type',
            width: 90,
            render: (c) => (
              <Badge variant="light" color="indigo" leftSection={<IconFileText size={12} />}>
                {formatContractType(c.contract_type)}
              </Badge>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            width: 120,
            render: (c) => {
              const b = formatContractStatus(c.status);
              return (
                <Badge variant="light" color={b.color}>
                  {b.label}
                </Badge>
              );
            },
          },
          {
            accessor: 'effective_date',
            title: 'Term',
            width: 200,
            render: (c) => {
              const start = c.effective_date ?? '—';
              const end = c.expiration_date ?? 'perpetual';
              return (
                <Text size="xs" c="dimmed">
                  {start} → {end}
                </Text>
              );
            },
          },
          {
            accessor: 'recorded',
            title: 'Recorded',
            width: 90,
            render: (c) =>
              c.recorded ? (
                <Badge variant="dot" color="pine">
                  yes
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'tract_count',
            title: 'Tracts',
            width: 70,
            textAlign: 'right',
            render: (c) => c.tract_count ?? 0,
          },
          {
            accessor: 'party_count',
            title: 'Parties',
            width: 70,
            textAlign: 'right',
            render: (c) => c.party_count ?? 0,
          },
          {
            accessor: 'document_count',
            title: 'Docs',
            width: 60,
            textAlign: 'right',
            render: (c) => c.document_count ?? 0,
          },
        ]}
      />

      <ContractFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        contract={null}
        onSaved={(c) => {
          setDrawerOpen(false);
          navigate(`/contracts/${c.id}`);
        }}
      />
    </Stack>
  );
}
